import React, { useEffect, useRef, useState } from 'react';
import { getAvailableReports, reactToReport, ReportsReasonType, ReportType } from '../../api/reports';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select, Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Avatar
} from '@mui/material';
import styles from '../users/components/UsersTableComponent/UserTableComponent.module.scss';
import DoDisturbOnTwoToneIcon from '@mui/icons-material/DoDisturbOnTwoTone';
import HorizontalRuleTwoToneIcon from '@mui/icons-material/HorizontalRuleTwoTone';
import moment from 'moment';
import MenuItem from '@mui/material/MenuItem';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

const columns = [
  { id: 'reporter', label: 'Reporter' },
  { id: 'user', label: 'Whom reported' },
  { id: 'type', label: 'Place' },
  { id: 'message', label: 'Message' },
  { id: 'reason', label: 'Reason' },
  { id: 'createdDate', label: 'Date and time' }
];

const filterByTypeList = [
  { id: 'CHAT_REPORT', label: 'Chat report' },
  { id: 'PROFILE_REPORT', label: 'Profile report' },
];

const filterByReasonList = [
  { id: 'ABUSE', label: 'Reason' },
  { id: 'SPAM', label: 'Spam' },
  { id: 'NUDITY', label: 'Nudity' },
  { id: 'HAJEM_RULES', label: 'Hajem rules' },
];

const Reports = () => {
  const [reports, setReports] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [distanceBottom, setDistanceBottom] = useState(0);
  const tableEl = useRef<any>(null);
  const [isReactedReport, setIsReactedReport] = useState(false);
  const [filterByType, setFilterByType] = useState<ReportType | null>(null);
  const [filterByReason, setFilterByReason] = useState<ReportsReasonType | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
  const [alert, setAlert] = useState({
    isSuccess: false,
    message: ''
  });
  const [selectedReport, setSelectedReport] = useState<any>(null);
  
  useEffect(() => {
    (async () => {
      setReports(await getAvailableReports({
        reacted: isReactedReport,
        ...(!!filterByReason && {reason: filterByReason}),
        ...(!!filterByType && {type: filterByType})
      }));
    })();
    setHasMore(true);
  }, [isReactedReport, filterByType, filterByReason]);
  
  const handleReasonTextView = (reasonText: ReportsReasonType) => {
    switch (reasonText) {
      case 'ABUSE': return 'Abuse'
      case 'SPAM': return 'Spam'
      case 'NUDITY': return 'Nudity'
      case 'HAJEM_RULES': return 'Hajem rules'
      default: return reasonText
    }
  };
  
  const handleTypeTextView = (reportText: ReportType) => {
    switch (reportText) {
      case 'CHAT_REPORT': return 'Chat report'
      case 'PROFILE_REPORT': return 'Profile report'
      default: return reportText
    }
  };
  
  const getMoreReports = async () => {
    const params = {
      reacted: isReactedReport,
      ...(!!filterByReason && {reason: filterByReason}),
      ...(!!filterByType && {type: filterByType}),
      cursor: reports[reports.length - 1].id,
    };
  
    const newReportsList = await getAvailableReports(params);
    setReports([...reports, ...newReportsList]);
    if (!newReportsList.length) setHasMore(false);
    setLoading(false);
  };
  
  const loadMore = () => {
    const loadItems = async () => {
      await new Promise(resolve =>
        setTimeout(() => {
          getMoreReports();
          resolve(true);
        }, 2000)
      )
    };
    
    loadItems();
  };
  
  useEffect(() => {
    const scrollListener = () => {
      let bottom = tableEl?.current?.scrollHeight - tableEl?.current?.clientHeight;
      
      if (!distanceBottom) {
        setDistanceBottom(Math.round(bottom * 0.2));
      }
      
      if ((tableEl.current.scrollTop > bottom - distanceBottom) && hasMore && !loading) {
        loadMore();
        setLoading(true);
      }
    };
    
    if (tableEl && tableEl.current) {
      tableEl.current.addEventListener('scroll', scrollListener, false);
      
      return () => tableEl?.current?.removeEventListener('scroll', scrollListener, false);
    }
  }, [tableEl.current, reports, distanceBottom]);
  
  const handleRowItem = (valueType: string, value: any, row?: any) => {
    switch (valueType) {
      case 'reporter': {
        return <div>
          <p>{value.username}</p>
          <p>{value.email}</p>
        </div>
      }
      case 'user': {
        return <div>
          <p>{value.username}</p>
          <p>{value.email}</p>
        </div>
      }
      case 'message': {
        return <p className="max-w-[200px]">{value}</p>
      }
      case 'reason': {
        return <p>{handleReasonTextView(value)}</p>
      }
      case 'type': {
        return <p>{handleTypeTextView(value)}</p>
      }
      case 'createdDate': {
        return <span>{moment(value.toString()).format('LLLL')}</span>
      }
      default: return <span>{value || <HorizontalRuleTwoToneIcon color="disabled" />}</span>;
    }
  };
  
  const handleTooltipPreview = (valueType: string, value: any) => {
    switch (valueType) {
      case 'reporter': {
        return <div>
          <p>{value.username}</p>
          <p>{value.email}</p>
        </div>
      }
      case 'user': {
        return <div>
          <p>{value.username}</p>
          <p>{value.email}</p>
        </div>
      }
      case 'message': {
        return <p className="max-w-[200px]">{value}</p>
      }
      case 'reason': {
        return <p>{handleReasonTextView(value)}</p>
      }
      case 'type': {
        return <p>{handleTypeTextView(value)}</p>
      }
      case 'createdDate': {
        return <span>{moment(value.toString()).format('LLLL')}</span>
      }
      default: return <span>{value || <DoDisturbOnTwoToneIcon color="warning"/>}</span>;
    }
  };
  
  const handleReactToReport = async (reaction: 'DECLINE_REPORT' | 'BLOCK_CHAT') => {
    try {
      await reactToReport(selectedReport.id, reaction);
      setReports(reports?.filter((item: any) => item.id !== selectedReport.id));
      setAlert({ isSuccess: true, message: 'You have successfully responded to the report'});
    } catch (e) {
      setAlert({ isSuccess: false, message: 'Something went wrong, response was not uploaded, please try again later'});
    }
    
    setIsOpenSnackbar(true);
    setIsOpenDialog(false);
  };
  
  return (
    <div>
      <div className="flex items-center">
        <FormControlLabel
          labelPlacement="start"
          className="block w-max text-xl !ml-5"
          control={
            <Switch
              defaultChecked={false}
              checked={isReactedReport}
              onChange={(event) => setIsReactedReport(event.target.checked)}
            />
          }
          label="Is reacted report"
        />
        <div className={styles.filterLine} />
        <FormControl sx={{ m: 1, width: 200 }}>
          <InputLabel id="filterByType">Filter by type</InputLabel>
          <Select
            labelId="filterByType"
            id="filter-by-type"
            label="Filter by type"
            value={filterByType}
            onChange={(event) => setFilterByType(event.target.value as ReportType)}
          >
            {filterByTypeList.map((value, index) => {
              return <MenuItem value={value.id} key={index.toString()}>{value.label}</MenuItem>
            })}
          </Select>
        </FormControl>
        <div className={styles.filterLine} />
        <FormControl sx={{ m: 1, width: 200 }}>
          <InputLabel id="filterByReason">Filter by reason</InputLabel>
          <Select
            labelId="filterByReason"
            id="filter-by-reason"
            label="Filter by reason"
            value={filterByReason}
            onChange={(event) => setFilterByReason(event.target.value as ReportsReasonType)}
          >
            {filterByReasonList.map((value, index) => {
              return <MenuItem value={value.id} key={index.toString()}>{value.label}</MenuItem>
            })}
          </Select>
        </FormControl>
        {(filterByReason || filterByType || isReactedReport) && (
          <>
            <div className={styles.filterLine} />
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                setFilterByReason(null);
                setFilterByType(null);
                setIsReactedReport(false);
              }}
            >
              Clear filters
              <FilterAltOffIcon color="error" className="ml-3" />
            </Button>
          </>
        )}
      </div>
      <div className="border-t-[1px] border-solid border-[#808080] pt-3 mt-5">
        {reports.length === 0 && (
          <p className="">No reports available...</p>
        )}
        <TableContainer sx={{ maxHeight: "65vh", width: '100%', position: 'relative', marginBottom: '20px' }} ref={tableEl}>
          <Table stickyHeader aria-labelledby="tableTitle" size={"small"} className={styles.table}>
            <TableHead>
              <TableRow>
                {reports.length > 0 && columns.map((item: any) => (
                  <TableCell
                    key={item.id}
                    align="center"
                    style={{ minWidth: 50 }}
                  >
                    <TableSortLabel
                      className="capitalize font-bold"
                    >
                      {item.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((row: any, index: number) => {
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={index.toString()}
                    className={styles.tableRow}
                    onClick={() => {
                      if (!isReactedReport) {
                        setSelectedReport(row);
                        setIsOpenDialog(true);
                      }
                    }}
                  >
                    {columns.map((column) => {
                      return (
                        <TableCell key={column.id} align="center" className={styles.rowItemContainer}>
                          <Tooltip title={handleTooltipPreview(column.id, row[column.id])} placement="top">
                            {handleRowItem(column.id, row[column.id], row)}
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <div className={styles.loadingContainer}>
          {loading && <CircularProgress className={styles.loading} />}
        </div>
      </div>
      <Dialog open={isOpenDialog} onClose={() => setIsOpenDialog(false)}>
        <DialogTitle className="!font-bold !text-3xl ">React to the report</DialogTitle>
        <DialogContent>
          <div className="grid grid-cols-2 gap-x-8 mt-5">
            <span className="block font-bold text-sm">Reporter: </span>
            <div className="flex items-center">
              <Avatar
                alt={selectedReport?.reporter?.username}
                src={selectedReport?.reporter?.coverImageUrl}
                sx={{ width: 56, height: 56 }}
              >
                {selectedReport?.reporter?.username[0]}
              </Avatar>
              <div className="ml-5">
                <p>{selectedReport?.reporter?.username}</p>
                <p>{selectedReport?.reporter?.email}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-8 mt-5">
            <span className="block font-bold text-sm">Whom reported: </span>
            <div className="flex items-center">
              <Avatar
                alt={selectedReport?.user?.username}
                src={selectedReport?.user?.coverImageUrl}
                sx={{ width: 56, height: 56 }}
              >
                {selectedReport?.user?.username[0]}
              </Avatar>
              <div className="ml-5">
                <p>{selectedReport?.user?.username}</p>
                <p>{selectedReport?.user?.email}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-8 mt-5">
            <span className="block font-bold text-sm">Report reason: </span>
            <span>{handleReasonTextView(selectedReport?.reason)}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-8 mt-5">
            <span className="block font-bold text-sm">Report type: </span>
            <span>{handleTypeTextView(selectedReport?.type)}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-8 mt-5">
            <span className="block font-bold text-sm">Report message: </span>
            <span>{selectedReport?.message}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-8 mt-5">
            <Button variant="contained" onClick={() => handleReactToReport('DECLINE_REPORT')}>Decline report</Button>
            <Button variant="contained" onClick={() => handleReactToReport('BLOCK_CHAT')}>Block chat</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Snackbar
        sx={{ height: "5%" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={isOpenSnackbar}
        autoHideDuration={5000}
        onClose={() => setIsOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setIsOpenSnackbar(false)}
          severity={alert.isSuccess ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </div>
  )
};

export default Reports;